import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { inputStyle, mono, primaryButton, secondaryButton } from '../proxy-vault/ui';

/** Mirrors the server's `MAX_SUMMARY_CHARS` (skills-repository.schema.ts). */
const MAX_SUMMARY_CHARS = 200;

export type UploadTarget = { kind: 'create' } | { kind: 'version'; skillName: string };

/**
 * Upload a skill bundle (zip) — to create a skill, or to publish a new version
 * of an existing one (which becomes current).
 *
 * Validation is the server's alone (single source of truth): a refused bundle
 * comes back as 422 problem+json with one `invalid_params` row per finding, and
 * `describeApiError` renders every row, so the operator reads "SKILL.md —
 * missing at the bundle root" instead of a generic failure. The parent owns the
 * mutation (`onUpload`) so the list/detail it holds refresh from the same
 * response; resolving closes the dialog, rejecting keeps it open with the error.
 */
export function SkillUploadModal({
  target,
  onCancel,
  onUpload,
}: {
  target: UploadTarget;
  onCancel: () => void;
  onUpload: (bundle: File, summary: string) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onUpload(file, summary.trim());
    } catch (err) {
      setError(describeApiError(err, String(err)));
      setBusy(false);
    }
  };

  const canSubmit = file !== null && summary.length <= MAX_SUMMARY_CHARS && !busy;
  const title = target.kind === 'create' ? 'Upload a skill' : `New version of ${target.skillName}`;

  return (
    <div
      role="dialog"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
      }}
    >
      <div
        style={{
          width: 460,
          maxWidth: '92vw',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.5 }}>
          A zip with <span style={{ fontFamily: mono }}>SKILL.md</span> at its root (or inside one
          top-level folder), plus optional <span style={{ fontFamily: mono }}>scripts/</span>,{' '}
          <span style={{ fontFamily: mono }}>references/</span> and{' '}
          <span style={{ fontFamily: mono }}>assets/</span>. The skill&apos;s name and description come
          from the SKILL.md frontmatter.
          {target.kind === 'version' && (
            <>
              {' '}
              The bundle&apos;s name must stay{' '}
              <span style={{ fontFamily: mono }}>{target.skillName}</span>.
            </>
          )}
        </div>
        <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
          Bundle (.zip)
          <input
            data-testid="skrepo-upload-file"
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'block', marginTop: 4, fontSize: 12, fontFamily: 'inherit', color: 'var(--text)' }}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
          Summary (optional, what changed — {summary.length}/{MAX_SUMMARY_CHARS})
          <input
            data-testid="skrepo-upload-summary"
            style={{ ...inputStyle, width: '100%', marginTop: 4, boxSizing: 'border-box' }}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={target.kind === 'create' ? 'initial upload' : 'fix probe timeout'}
          />
        </label>
        {error && (
          <div
            data-testid="skrepo-upload-error"
            style={{ fontSize: 12, color: 'var(--kind-failure)', whiteSpace: 'pre-line', lineHeight: 1.5 }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={secondaryButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="skrepo-upload-submit"
            style={{ ...primaryButton, opacity: canSubmit ? 1 : 0.5 }}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {busy ? 'Uploading…' : target.kind === 'create' ? 'Upload' : 'Publish version'}
          </button>
        </div>
      </div>
    </div>
  );
}
